/* AL Project Advisor — floating AI shopping advisor.
   Web Component. Progressive enhancement: boots on first interaction / idle.
   Talks to the Vercel backend; turns a confirmed kit into a real cart add.
   Each visitor is assigned one team name (persona) for the session; the header, greeting
   and composer use it, and the model is told who it is in the first turn. */
/* eslint-disable */
if (!customElements.get('al-project-advisor')) {
  customElements.define(
    'al-project-advisor',
    class AlProjectAdvisor extends HTMLElement {
      connectedCallback() {
        this.endpoint = this.getAttribute('data-endpoint');
        this.names = (this.getAttribute('data-names') || 'Claire, Charlie, Daniel')
          .split(',')
          .map((n) => n.trim())
          .filter(Boolean);
        this.role = this.getAttribute('data-role') || 'Sales & Customer Services Executive';
        this.greetingTpl =
          this.getAttribute('data-greeting') ||
          'Hi, I’m {name} from the Artificial Living team 👋 What can I help you with today?';
        this.placeholderTpl = this.getAttribute('data-placeholder') || 'Message {name}…';
        this.phone = this.getAttribute('data-phone') || '0161 509 8637';
        this.launcherTpl = this.getAttribute('data-launcher-label') || 'Need Some Help?';
        // reply pacing: minimum total time from send to reveal, scaled by reply length
        this.delayMin = Math.max(0, Number(this.getAttribute('data-delay-min') || 5));
        this.delayMax = Math.max(this.delayMin, Number(this.getAttribute('data-delay-max') || 10));
        this.messages = []; // model-facing history (returned + replayed each turn)
        this.transcript = []; // displayed bubbles/kits, for restoring the conversation
        this.persona = null; // the team name this visitor is talking to, fixed for the session
        this.booted = false;
        this.busy = false;
        this._key = 'apa-chat-v3'; // bumped for personas: old sessions had no name and a generic greeting
        this._restore();
        if (!this.persona || !this.names.includes(this.persona)) {
          this.persona = this.names[Math.floor(Math.random() * this.names.length)] || 'Claire';
          this._save();
        }
        this._renderLauncher();
      }

      // ---------- persona ----------
      _t(tpl) {
        return String(tpl || '').replace(/\{name\}/g, this.persona);
      }
      // Sent once, folded into the visitor's first message. Tells the model who it is and how
      // to write. Never displayed: the transcript keeps the visitor's plain text.
      _personaNote() {
        return (
          `[Context for this chat, not visible to the customer: the customer is talking to ${this.persona}, ` +
          `${this.role} at Artificial Living. Reply as ${this.persona}. Write like a person typing in a live chat: ` +
          `plain UK English, short sentences, no dashes, no bullet points, no bold, no markdown, no headings. ` +
          `Use at most one emoji in the whole conversation. Do not open with "Great question" or "Happy to help". ` +
          `Do not end every message with a question. Do not repeat your name after the first message. ` +
          `If the customer asks whether they are talking to a real person, say honestly that you are an AI assistant ` +
          `on the team and that a colleague is on ${this.phone}.]\n\n`
        );
      }

      // ---------- launcher ----------
      _renderLauncher() {
        const btn = document.createElement('button');
        btn.className = 'apa-launcher';
        btn.type = 'button';
        btn.setAttribute('aria-haspopup', 'dialog');
        btn.setAttribute('aria-expanded', 'false');
        btn.innerHTML = `<span class="apa-emoji" aria-hidden="true">👋</span><span class="apa-label"></span>`;
        btn.querySelector('.apa-label').textContent = this._t(this.launcherTpl);
        btn.addEventListener('click', () => this.open());
        this._launcher = btn;
        this.appendChild(btn);
      }

      // ---------- panel ----------
      open() {
        if (!this.booted) this._buildPanel();
        this._panel.hidden = false;
        this._launcher.setAttribute('aria-expanded', 'true');
        this._lastFocus = document.activeElement;
        document.documentElement.classList.add('apa-locked'); // freeze the page behind the sheet
        this._bindViewport(); // keep the composer above the mobile keyboard
        this._input.focus();
        document.addEventListener('keydown', this._onKeydown);
        if (!this._log.children.length) {
          if (this.transcript.length) this._replay();
          else this._pushBot(this._t(this.greetingTpl), true);
        }
      }

      close() {
        if (!this._panel) return;
        this._panel.hidden = true;
        this._launcher.setAttribute('aria-expanded', 'false');
        document.documentElement.classList.remove('apa-locked');
        this._unbindViewport();
        document.removeEventListener('keydown', this._onKeydown);
        if (this._lastFocus) this._lastFocus.focus();
      }

      _buildPanel() {
        this.booted = true;
        this._onKeydown = (e) => {
          if (e.key === 'Escape') this.close();
          if (e.key === 'Tab') this._trapFocus(e);
        };

        const panel = document.createElement('div');
        panel.className = 'apa-panel';
        panel.hidden = true;
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'false');
        panel.setAttribute('aria-label', `Chat with ${this.persona}`);
        panel.innerHTML = `
          <header class="apa-head">
            <div class="apa-head__id">
              <span class="apa-avatar" aria-hidden="true"></span>
              <div class="apa-who"><strong class="apa-who__name"></strong><small class="apa-who__role"></small></div>
            </div>
            <button class="apa-close" type="button" aria-label="Close chat">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </header>
          <div class="apa-log" data-log role="log" aria-live="polite" aria-atomic="false"></div>
          <div class="apa-chips" data-chips>
            <button type="button" data-chip="Can you give me some product info?">Product info</button>
            <button type="button" data-chip="What are your delivery times?">Delivery times</button>
            <button type="button" data-chip="I'm a trade customer">I'm trade</button>
          </div>
          <form class="apa-form" data-form>
            <input class="apa-input" data-input type="text" autocomplete="off" aria-label="Message" />
            <button class="apa-send" type="submit" aria-label="Send">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
            </button>
          </form>`;
        this.appendChild(panel);

        this._panel = panel;
        this._log = panel.querySelector('[data-log]');
        this._input = panel.querySelector('[data-input]');
        this._chips = panel.querySelector('[data-chips]');
        panel.querySelector('.apa-avatar').textContent = this.persona.charAt(0).toUpperCase();
        panel.querySelector('.apa-who__name').textContent = this.persona;
        panel.querySelector('.apa-who__role').textContent = this.role;
        this._input.placeholder = this._t(this.placeholderTpl);
        panel.querySelector('.apa-close').addEventListener('click', () => this.close());
        panel.querySelector('[data-form]').addEventListener('submit', (e) => {
          e.preventDefault();
          this._submit(this._input.value);
        });
        this._chips.querySelectorAll('[data-chip]').forEach((c) =>
          c.addEventListener('click', () => this._submit(c.getAttribute('data-chip')))
        );
        // when the keyboard opens, make sure the latest message stays in view
        this._input.addEventListener('focus', () => setTimeout(() => this._scroll(), 100));
      }

      // ---------- mobile keyboard / viewport ----------
      // On mobile the panel is anchored to the top and sized to the live visualViewport
      // height, so it shrinks with the on-screen keyboard and the composer never hides
      // behind it. No-op on desktop (CSS keeps the floating panel) and where the API is absent.
      _bindViewport() {
        const vv = window.visualViewport;
        if (!vv) return;
        if (!this._onVV) {
          this._onVV = () => {
            if (!this._panel || this._panel.hidden) return;
            const mobile = window.matchMedia('(max-width: 480px)').matches;
            if (!mobile) {
              this._panel.style.height = '';
              this._panel.style.paddingBottom = '';
              return;
            }
            // Keep the panel covering the FULL screen (so the page can never bleed through),
            // and reserve space at the bottom equal to the keyboard height so the composer
            // rides above it. innerHeight = layout viewport (unaffected by the keyboard);
            // vv.height = the visible area, so the difference is the keyboard inset.
            const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
            this._panel.style.height = `${window.innerHeight}px`;
            this._panel.style.paddingBottom = `${inset}px`;
            this._scroll();
          };
        }
        vv.addEventListener('resize', this._onVV); // same ref → no duplicate listeners across opens
        vv.addEventListener('scroll', this._onVV);
        this._onVV();
      }

      _unbindViewport() {
        const vv = window.visualViewport;
        if (vv && this._onVV) {
          vv.removeEventListener('resize', this._onVV);
          vv.removeEventListener('scroll', this._onVV);
        }
        if (this._panel) {
          this._panel.style.height = '';
          this._panel.style.paddingBottom = '';
          this._panel.style.transform = '';
        }
      }

      // ---------- conversation ----------
      async _submit(text) {
        text = (text || '').trim();
        if (!text || this.busy) return;
        this._input.value = '';
        this._chips.hidden = true;
        this._pushUser(text);
        const first = this.messages.length === 0;
        this.messages.push({ role: 'user', content: (first ? this._personaNote() : '') + text });
        await this._send();
      }

      async _send() {
        this.busy = true;
        const started = Date.now();
        const typing = this._typing();
        try {
          const res = await fetch(this.endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ messages: this.messages }),
          });
          const data = await res.json();
          if (!res.ok || data.error) {
            typing.remove();
            this._pushBot(
              `Sorry, something went wrong on my end. Give the team a call on ${this.phone} and they’ll sort it straight away.`
            );
            return;
          }
          // Hold the reply behind the typing dots until a person could plausibly have written it.
          // History is committed after the reveal so a mid-pause navigation never strands a
          // reply the visitor didn't see.
          await this._humanPause(started, data.reply);
          typing.remove();
          this.messages = data.messages || this.messages; // keep server-maintained history
          this._save();
          if (data.reply) this._pushBot(data.reply);
          if (data.cart && data.cart.items?.length) this._renderKit(data.cart);
        } catch (e) {
          typing.remove();
          this._pushBot(
            `Sorry, I can’t connect at the moment. Call us on ${this.phone} and we’ll sort it.`
          );
        } finally {
          this.busy = false;
        }
      }

      // Total send→reveal time: delayMin for a one-liner, rising to delayMax by ~400 characters,
      // plus a little jitter so it never feels metronomic. Backend latency counts towards it.
      _humanPause(started, reply) {
        const min = this.delayMin * 1000;
        const max = this.delayMax * 1000;
        const len = String(reply || '').length;
        const jitter = (Math.random() - 0.5) * 1200;
        const target = min + (max - min) * Math.min(1, len / 400) + jitter;
        const remaining = target - (Date.now() - started);
        return remaining > 0 ? new Promise((r) => setTimeout(r, remaining)) : Promise.resolve();
      }

      // ---------- cart ----------
      // The kit card's CTA is a Shopify cart permalink (/cart/id:qty,…): tapping it loads the
      // whole kit into the basket and lands on the cart page (checkout button right there).
      // Same-origin link → no dependency on the theme's cart drawer / eventBus.
      _renderKit(cart, replay) {
        const wrap = document.createElement('div');
        wrap.className = 'apa-kit';
        const rows = cart.items
          .map((i) => `<li><span>${this._esc(i.label)}</span><b>×${i.quantity}</b></li>`)
          .join('');
        const href = this._cartPermalink(cart.items);
        wrap.innerHTML = `
          <ul class="apa-kit__list">${rows}</ul>
          ${href
            ? `<a class="apa-kit__add" href="${href}">Add to basket &amp; checkout →</a>`
            : `<div class="apa-kit__note">Tap any item’s product page to add it, or call us on ${this._esc(this.phone)}.</div>`}`;
        this._log.appendChild(wrap);
        this._scroll();
        if (!replay) { this.transcript.push({ t: 'kit', items: cart.items }); this._save(); }
      }

      _cartPermalink(items) {
        const parts = (items || [])
          .filter((i) => Number(i.id) > 0 && Number(i.quantity) > 0)
          .map((i) => `${Number(i.id)}:${Number(i.quantity)}`);
        return parts.length ? `/cart/${parts.join(',')}` : '';
      }

      // ---------- render helpers ----------
      _pushUser(text) {
        this._bubble('user', text);
      }
      _pushBot(text, isGreeting) {
        this._bubble('bot', text);
        if (isGreeting) this.messages = []; // greeting is UI-only, not model history
      }
      _bubble(who, text, replay) {
        const el = document.createElement('div');
        el.className = `apa-msg apa-msg--${who}`;
        if (who === 'bot') text = this._humanise(text);
        el.innerHTML = this._linkify(text);
        this._log.appendChild(el);
        this._scroll();
        if (!replay) { this.transcript.push({ t: 'msg', who, text }); this._save(); }
      }
      // ---------- persistence (survives close/open + page navigation in the session) ----------
      _save() {
        try {
          sessionStorage.setItem(
            this._key,
            JSON.stringify({ persona: this.persona, messages: this.messages, transcript: this.transcript })
          );
        } catch (e) {}
      }
      _restore() {
        try {
          const s = JSON.parse(sessionStorage.getItem(this._key) || 'null');
          if (s && Array.isArray(s.transcript)) { this.messages = s.messages || []; this.transcript = s.transcript; }
          if (s && typeof s.persona === 'string') this.persona = s.persona;
        } catch (e) {}
      }
      _replay() {
        this.transcript.forEach((e) =>
          e.t === 'kit' ? this._renderKit({ items: e.items }, true) : this._bubble(e.who, e.text, true)
        );
      }
      // Safety net behind the persona note: dashes become commas, stray markdown bold is
      // dropped, so a reply never shows "**" or an em dash even if the model slips.
      _humanise(s) {
        return String(s == null ? '' : s)
          .replace(/\s*[—–]\s*/g, ', ')
          .replace(/\*\*(.+?)\*\*/g, '$1')
          .replace(/^\s*[-*]\s+/gm, '')
          // drop assistant-style openers the note asks it to avoid, e.g. "Great question! So..."
          .replace(/^\s*(?:great|good|brilliant|excellent)\s+question[!.,]?\s*(?:so\b,?\s*)?/i, '')
          .replace(/^\s*happy\s+to\s+help[!.,]?\s*/i, '')
          .replace(/^([a-z])/, (m) => m.toUpperCase());
      }
      _linkify(s) {
        // escape first, then make URLs clickable — never inject raw model text as HTML
        return this._esc(s).replace(
          /(https?:\/\/[^\s<]+[^\s<.,;:!?)])/g,
          '<a href="$1" target="_blank" rel="noopener" style="color:inherit;text-decoration:underline;font-weight:600">$1</a>'
        );
      }
      _typing() {
        const el = document.createElement('div');
        el.className = 'apa-msg apa-msg--bot apa-typing';
        el.innerHTML = '<span></span><span></span><span></span>';
        this._log.appendChild(el);
        this._scroll();
        return el;
      }
      _scroll() {
        this._log.scrollTop = this._log.scrollHeight;
      }
      _esc(s) {
        const d = document.createElement('div');
        d.textContent = s == null ? '' : String(s);
        return d.innerHTML;
      }
      _trapFocus(e) {
        const f = this._panel.querySelectorAll(
          'button, input, [href], [tabindex]:not([tabindex="-1"])'
        );
        if (!f.length) return;
        const first = f[0],
          last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }
  );
}
